import { useState, useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  makeStyles,
  Box,
  Button,
} from "@material-ui/core";

import Api from "lib/api/httpRequestHelper";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import HeaderClientes from "components/_refactor/headers/clients";
import useStructureQueries from "hooks/useStructureQueries";

import componentStyles from "assets/theme/views";
import themeColorsDesingSystem from "assets/theme/ds-colors";

import { DEFAULT_REPRESENTATIVE_LIST_FILTERS } from "../shared/utils/representative-helpers";
import { buildClientePagedQuery } from "../shared/utils/representative-api-params";
import { REPRESENTATIVE_PAGE_SIZE } from "../shared/utils/representative-helpers";

import RepresentativeFilters from "../shared/components/representative-filters";
import RepresentativeSelectionList, {
  useRepresentativeListSearch,
} from "../shared/components/representative-selection-list";

const useStyles = makeStyles(componentStyles);

export default function PublicarVitrine() {
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState({
    ...DEFAULT_REPRESENTATIVE_LIST_FILTERS,
  });
  const [grupoEconomicoOptions, setGrupoEconomicoOptions] = useState([]);
  const [razaoSocialOptions, setRazaoSocialOptions] = useState([]);

  const history = useHistory();
  const searchParams = new URLSearchParams(window.location.search);
  const classes = useStyles();
  const id = searchParams.get("id");
  const snackbar = useSnackbar();
  const { getEconomicGroups, getStores } = useStructureQueries();

  const selectedIds = useMemo(
    () => new Set(selectedCustomers),
    [selectedCustomers],
  );

  const loadFilterOptions = useCallback(async () => {
    try {
      const [groupsRes, storesRes] = await Promise.all([
        getEconomicGroups({}),
        getStores({}),
      ]);

      setGrupoEconomicoOptions(
        (groupsRes?.data ?? []).map((g) => ({
          Id: g.Id ?? g.id,
          Nome: g.Nome ?? g.nome,
        })),
      );
      setRazaoSocialOptions(
        (storesRes?.data ?? []).map((s) => ({
          Id: s.Id ?? s.id,
          Nome: (s.Nome ?? s.nome ?? "").trim(),
        })),
      );
    } catch {
      setGrupoEconomicoOptions([]);
      setRazaoSocialOptions([]);
    }
  }, [getEconomicGroups, getStores]);

  const fetchPage = useCallback(
    async (pageToLoad, filtersToUse) => {
      if (!id) {
        setItems([]);
        setTotalItems(0);
        setTotalPages(0);
        return;
      }

      setIsLoading(true);
      try {
        const query = buildClientePagedQuery(filtersToUse, {
          idVitrineVirtualB2b: id,
          page: pageToLoad,
          pageSize: REPRESENTATIVE_PAGE_SIZE,
        });

        const response = await Api.get(
          "list-b2b-showcase-publish-clients",
          "showcaseB2B",
          `?${query}`,
          true,
        );

        const result = response?.Result;
        const list = (result?.Lista ?? result?.Result ?? []).map((cliente) => ({
          ...cliente,
          __selectionId: cliente.IdPessoaLoja,
        }));

        setItems(list);
        setTotalItems(result?.TotalRegistros ?? 0);
        setTotalPages(result?.TotalDePaginas ?? 0);
        setPage(result?.Pagina ?? pageToLoad);
      } catch (error) {
        console.error(error);
        setItems([]);
        setTotalItems(0);
        setTotalPages(0);
        snackbar.showMessage("Erro ao buscar clientes. Tente novamente.", {
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [id, snackbar],
  );

  const handleFiltersChange = useCallback((updates) => {
    setFilters((prev) => ({ ...prev, ...updates }));
    setPage(0);
  }, []);

  const onDebouncedSearch = useCallback((value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPage(0);
  }, []);

  const { searchInput, handleSearchChange } = useRepresentativeListSearch(
    filters.search ?? "",
    onDebouncedSearch,
  );

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    fetchPage(page, filters);
  }, [fetchPage, page, filters]);

  const allVisibleSelected =
    items.length > 0 && items.every((c) => selectedIds.has(c.__selectionId));
  const someVisibleSelected = items.some((c) =>
    selectedIds.has(c.__selectionId),
  );

  const handleToggleItem = useCallback((item) => {
    const clienteId = item.__selectionId;
    setSelectedCustomers((prev) =>
      prev.includes(clienteId)
        ? prev.filter((pid) => pid !== clienteId)
        : [...prev, clienteId],
    );
  }, []);

  const handleToggleAllVisible = useCallback(
    (visibleIds) => {
      if (allVisibleSelected) {
        const hide = new Set(visibleIds);
        setSelectedCustomers((prev) => prev.filter((pid) => !hide.has(pid)));
      } else {
        setSelectedCustomers((prev) =>
          Array.from(new Set([...prev, ...visibleIds])),
        );
      }
    },
    [allVisibleSelected],
  );

  const handlePublish = async () => {
    try {
      const response = await Api.post(
        "create-virtual-b2b-cliente",
        "showcaseB2B",
        {
          idVitrineVirtualB2b: Number(id),
          idClientes: selectedCustomers.toString(),
        },
        true,
      );

      if (response.Success) {
        history.push("/admin/visualizar-vitrines-b2b");
        snackbar.showMessage(response.Result.MessageResponse, {
          severity: "success",
        });
      }
    } catch (error) {
      console.error(error);
      snackbar.showMessage("Erro ao publicar vitrine. Tente novamente.", {
        severity: "error",
      });
    }
  };

  return (
    <>
      <HeaderClientes
        section="Publicação da Vitrine – Público Alvo"
        subsection="Vitrine"
      />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <Card classes={{ root: classes.cardRoot }}>
          <CardContent>
            <Grid
              container
              alignItems="center"
              direction="row"
              justify="space-between"
            >
              <Typography variant="h2">
                Publicação da Vitrine – Público Alvo
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>

            <RepresentativeFilters
              isLoading={isLoading}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              grupoEconomicoOptions={grupoEconomicoOptions}
              razaoSocialOptions={razaoSocialOptions}
            />

            <RepresentativeSelectionList
              isLoading={isLoading}
              items={items}
              totalItems={totalItems}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              selectedIds={selectedIds}
              onToggleItem={handleToggleItem}
              onToggleAllVisible={handleToggleAllVisible}
              allVisibleSelected={allVisibleSelected}
              someVisibleSelected={someVisibleSelected}
              searchValue={searchInput}
              onSearchChange={handleSearchChange}
              emptyMessage="Nenhum cliente encontrado com os filtros aplicados."
              selectionFooter={
                selectedCustomers.length > 0 ? (
                  <Typography
                    variant="caption"
                    style={{
                      display: "block",
                      marginTop: 8,
                      color: themeColorsDesingSystem.gray.dark,
                    }}
                  >
                    {selectedCustomers.length} cliente(s) selecionado(s)
                  </Typography>
                ) : null
              }
            />

            <Box
              display="flex"
              width="100%"
              style={{ marginTop: "2rem", gap: "2rem" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handlePublish}
                disabled={isLoading || selectedCustomers.length === 0}
              >
                Publicar
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => history.push("/admin/visualizar-vitrines-b2b")}
                disabled={isLoading}
              >
                Voltar
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
